// PUBLIC LINKS TAB - ISOLATED ADMIN COMPONENT
// Manages expiring public video links

import React, { useState, useEffect } from 'react'
import { Share2, Copy, ExternalLink, Clock, Eye, Trash2, Plus } from 'lucide-react'
import { toast } from 'react-hot-toast'

const PublicLinksTab = () => {
  const [videos, setVideos] = useState([])
  const [publicLinks, setPublicLinks] = useState([])
  const [loading, setLoading] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState('')

  const [createForm, setCreateForm] = useState({
    title: '',
    expires_in_days: 7,
    max_views: 10
  })

  useEffect(() => {
    fetchVideos()
    fetchPublicLinks()

    // Auto-refresh every 30 seconds to show real-time view updates
    const interval = setInterval(() => {
      fetchPublicLinks()
    }, 30000)

    return () => clearInterval(interval)
  }, [])

  const fetchVideos = async () => {
    try {
      // Use standard token from admin login
      const token = localStorage.getItem('token')
      const response = await fetch('/.netlify/functions/videos-list', {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setVideos(data.videos || [])
      }
    } catch (error) {
      console.error('Failed to fetch videos:', error)
    }
  }

  const fetchPublicLinks = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/.netlify/functions/list-public-links', {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setPublicLinks(data.links || [])
      } else {
        console.error('Failed to fetch public links')
        setPublicLinks([])
      }
    } catch (error) {
      console.error('Failed to fetch public links:', error)
      setPublicLinks([])
    }
  }

  const handleCreateLink = async (videoId) => {
    setLoading(true)
    try {
      // Use standard token from admin login
      const token = localStorage.getItem('token')

      const response = await fetch('/.netlify/functions/create-public-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          video_id: videoId,
          title: createForm.title,
          expires_in_days: parseInt(createForm.expires_in_days),
          max_views: parseInt(createForm.max_views)
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create public link')
      }

      const data = await response.json()

      // Show success with copyable link
      toast.success('Public link created successfully!')

      // Copy to clipboard
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(data.link.url)
        toast.success('Link copied to clipboard!')
      }

      // Reset form
      setCreateForm({ title: '', expires_in_days: 7, max_views: 10 })
      setSelectedVideo('')
      setShowCreateForm(false)

      // Refresh the links list
      fetchPublicLinks()

      return data.link

    } catch (error) {
      console.error('Create link error:', error)
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied to clipboard!')
    } catch (error) {
      toast.error('Failed to copy link')
    }
  }

  const deleteLink = async (linkId, linkTitle) => {
    if (!confirm(`Are you sure you want to delete the public link for "${linkTitle}"?\n\nThis action cannot be undone and the link will stop working immediately.`)) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/.netlify/functions/delete-public-link', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ link_id: linkId })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete link')
      }

      toast.success('Public link deleted successfully!')

      // Refresh the links list
      fetchPublicLinks()

    } catch (error) {
      console.error('Delete link error:', error)
      toast.error(error.message)
    }
  }

  const formatTimeRemaining = (expiresAt) => {
    const now = new Date()
    const expires = new Date(expiresAt)
    const diffMs = expires - now

    if (diffMs <= 0) return 'Expired'

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

    if (days > 0) return `${days}d remaining`
    if (hours > 0) return `${hours}h remaining`
    return 'Soon'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white mb-2">Public Video Links</h2>
          <p className="text-gray-400 text-sm">
            Create expiring links for non-tech-savvy family members. No login required.
          </p>
        </div>

        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Create Link</span>
        </button>
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-white mb-4">Create Public Link</h3>

            {/* Video Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Select Video
              </label>
              <select
                value={selectedVideo}
                onChange={(e) => setSelectedVideo(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              >
                <option value="">Choose a video...</option>
                {videos.map(video => (
                  <option key={video.id} value={video.id}>
                    {video.title} ({(video.file_size / 1024 / 1024).toFixed(1)}MB)
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Title */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Custom Title (optional)
              </label>
              <input
                type="text"
                value={createForm.title}
                onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                placeholder="Leave empty to use video title"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              />
            </div>

            {/* Expiry Days */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Expires in days
              </label>
              <select
                value={createForm.expires_in_days}
                onChange={(e) => setCreateForm({ ...createForm, expires_in_days: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              >
                <option value={1}>1 day</option>
                <option value={3}>3 days</option>
                <option value={7}>7 days (default)</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>

            {/* Max Views */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Maximum views
              </label>
              <select
                value={createForm.max_views}
                onChange={(e) => setCreateForm({ ...createForm, max_views: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              >
                <option value={5}>5 views</option>
                <option value={10}>10 views (default)</option>
                <option value={25}>25 views</option>
                <option value={50}>50 views</option>
                <option value={100}>100 views</option>
              </select>
            </div>

            {/* Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => setShowCreateForm(false)}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => selectedVideo && handleCreateLink(selectedVideo)}
                disabled={!selectedVideo || loading}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-white font-medium mb-2">How it works:</h3>
        <ul className="text-gray-300 text-sm space-y-1">
          <li>• Create a public link for any video</li>
          <li>• Share the link via text, email, or messaging</li>
          <li>• Recipients can watch without creating accounts</li>
          <li>• Links automatically expire for security</li>
          <li>• Perfect for grandparents and non-tech family</li>
        </ul>
      </div>

      {/* Example */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-white font-medium mb-2">Example link:</h3>
        <div className="bg-gray-700 rounded p-3 font-mono text-sm text-gray-300">
          https://waltsportal.com/watch/abc123xyz456
        </div>
        <p className="text-gray-400 text-xs mt-2">
          Links are random and secure - no guessing possible
        </p>
      </div>

      {/* Existing Public Links */}
      {publicLinks.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-medium">Your Public Links ({publicLinks.length})</h3>

            {publicLinks.length > 1 && (
              <button
                onClick={() => {
                  if (confirm(`Are you sure you want to delete ALL ${publicLinks.length} public links?\n\nThis action cannot be undone and all links will stop working immediately.`)) {
                    publicLinks.forEach(link => deleteLink(link.id, link.title))
                  }
                }}
                className="text-red-400 hover:text-red-300 text-sm underline"
              >
                Delete All Links
              </button>
            )}
          </div>

          <div className="space-y-4">
            {publicLinks.map((link) => (
              <div key={link.id} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Title and Video Info */}
                    <h4 className="text-white font-medium mb-1">{link.title}</h4>
                    <p className="text-gray-400 text-sm mb-2">
                      Video: {link.video_filename}
                      {link.video_size && ` (${(link.video_size / 1024 / 1024).toFixed(1)}MB)`}
                    </p>

                    {/* Link URL */}
                    <div className="bg-gray-600 rounded p-2 mb-3">
                      <code className="text-green-400 text-sm break-all">{link.url}</code>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center space-x-4 text-sm">
                      <span className={`flex items-center space-x-1 ${
                        link.views_remaining > 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        <Eye className="w-4 h-4" />
                        <span>{link.current_views}/{link.max_views} views</span>
                      </span>

                      <span className={`flex items-center space-x-1 ${
                        link.is_expired ? 'text-red-400' : 'text-yellow-400'
                      }`}>
                        <Clock className="w-4 h-4" />
                        <span>{formatTimeRemaining(link.expires_at)}</span>
                      </span>

                      {!link.is_active && (
                        <span className="text-red-400 text-xs">INACTIVE</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => copyLink(link.url)}
                      className="p-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
                      title="Copy Link"
                    >
                      <Copy className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => window.open(link.url, '_blank')}
                      className="p-2 bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                      title="Test Link"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => deleteLink(link.id, link.title)}
                      className="p-2 bg-red-600 hover:bg-red-500 text-white rounded transition-colors"
                      title="Delete Link"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Last Accessed */}
                {link.last_accessed && (
                  <p className="text-gray-500 text-xs mt-2">
                    Last accessed: {new Date(link.last_accessed).toLocaleString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {publicLinks.length === 0 && (
        <div className="bg-gray-800 rounded-lg p-6 text-center">
          <Share2 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <h3 className="text-white font-medium mb-2">No Public Links Yet</h3>
          <p className="text-gray-400 text-sm">
            Create your first public link to start sharing videos with family
          </p>
        </div>
      )}
    </div>
  )
}

export default PublicLinksTab